(() => {
  const VERSION = '1.1.0';
  const SEARCH_PATH_RE = /\/search\/?$/;
  const SORTED_ATTR = 'data-f24-stock-search-sorted';
  const LIVE_PANEL_SELECTOR = '.f24-live-search-panel';
  const LIVE_PROBE_SECTION = 'f24-search-stock-probe';
  const liveRankCache = new Map();
  const livePanelState = new WeakMap();
  let sortScheduled = false;
  let observer = null;
  let livePanelDiscoveryObserver = null;

  const normalizeText = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('de-CH')
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .trim();

  const stockRank = (item) => {
    const text = normalizeText(item.textContent);
    if (text.includes('ab lager')) return 0;
    if (
      text.includes('ausverkauft') ||
      text.includes('nicht verfugbar') ||
      text.includes('nicht vorratig') ||
      text.includes('sold out')
    ) return 2;
    return 1;
  };

  const sortSearchGrid = () => {
    if (!SEARCH_PATH_RE.test(window.location.pathname)) return;
    const list = document.querySelector('#product-grid .product-grid');
    if (!list) return;

    const items = Array.from(list.children).filter((node) => node.matches('li.grid__item'));
    if (items.length < 2) {
      list.setAttribute(SORTED_ATTR, 'true');
      return;
    }

    const ranked = items.map((item, index) => ({ item, index, rank: stockRank(item) }));
    ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);

    if (!ranked.every((entry, index) => entry.item === items[index])) {
      const fragment = document.createDocumentFragment();
      ranked.forEach((entry) => fragment.appendChild(entry.item));
      list.appendChild(fragment);
    }
    list.setAttribute(SORTED_ATTR, 'true');
  };

  const scheduleSort = () => {
    if (sortScheduled) return;
    sortScheduled = true;
    requestAnimationFrame(() => {
      sortScheduled = false;
      sortSearchGrid();
    });
  };

  const addUnavailableLastToForms = () => {
    document.querySelectorAll('form').forEach((form) => {
      const queryInput = form.querySelector('input[name="q"], input[type="search"]');
      if (!queryInput) return;
      const action = form.getAttribute('action') || '';
      const isSearchForm = action.includes('/search') || form.matches('[role="search"]');
      if (!isSearchForm) return;

      let hidden = form.querySelector('input[name="options[unavailable_products]"]');
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = 'options[unavailable_products]';
        form.appendChild(hidden);
      }
      hidden.value = 'last';
    });
  };

  const enforceUnavailableLastOnSearchUrl = () => {
    if (!SEARCH_PATH_RE.test(window.location.pathname)) return false;
    const url = new URL(window.location.href);
    if (!url.searchParams.get('q')) return false;
    if (url.searchParams.get('options[unavailable_products]') === 'last') return false;
    url.searchParams.set('options[unavailable_products]', 'last');
    window.location.replace(url.toString());
    return true;
  };

  const observeSearchGrid = () => {
    observer?.disconnect();
    const container = document.querySelector('#ProductGridContainer');
    if (!container) return;
    observer = new MutationObserver(() => scheduleSort());
    observer.observe(container, { childList: true, subtree: true });
  };

  const shopRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const productHandleFromLink = (link) => {
    const explicit = link.dataset.f24ProductHandle;
    if (explicit) return explicit;
    try {
      const url = new URL(link.href, window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (_) {
      return '';
    }
  };

  const liveSearchInput = (panel) => {
    if (!panel.id) return null;
    try {
      return document.querySelector(`input[type="search"][aria-controls="${CSS.escape(panel.id)}"]`);
    } catch (_) {
      return null;
    }
  };

  const productSection = (panel) =>
    Array.from(panel.querySelectorAll('.f24-live-search__section')).find(
      (section) => normalizeText(section.querySelector('.f24-live-search__heading')?.textContent) === 'produkte'
    ) || null;

  const applyLiveRanks = (panel, query, rankMap) => {
    const section = productSection(panel);
    if (!section) return;
    const links = Array.from(section.querySelectorAll('a.f24-live-search__item[data-f24-search-item]'));
    if (links.length < 2) return;

    const ranked = links.map((link, index) => {
      const handle = productHandleFromLink(link);
      const rank = rankMap.has(handle) ? rankMap.get(handle) : 1;
      return { link, index, rank };
    });
    ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);

    if (!ranked.every((entry, index) => entry.link === links[index])) {
      ranked.forEach((entry) => section.appendChild(entry.link));
    }
    livePanelState.set(panel, { query, signature: ranked.map((entry) => productHandleFromLink(entry.link)).join('|') });
  };

  const fetchLiveRankMap = async (query) => {
    const key = query.toLocaleLowerCase('de-CH');
    if (liveRankCache.has(key)) return liveRankCache.get(key);

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('section_id', LIVE_PROBE_SECTION);

    const response = await fetch(`${shopRoot()}search/suggest?${params.toString()}`, {
      headers: { Accept: 'text/html' },
    });
    if (!response.ok) throw new Error(`F24 stock probe failed: ${response.status}`);

    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const map = new Map();
    doc.querySelectorAll('[data-f24-product-handle][data-f24-stock-rank]').forEach((node) => {
      const handle = String(node.dataset.f24ProductHandle || '').trim();
      const rank = Number.parseInt(node.dataset.f24StockRank || '1', 10);
      if (handle) map.set(handle, Number.isFinite(rank) ? rank : 1);
    });
    liveRankCache.set(key, map);
    return map;
  };

  const rankLivePanel = async (panel) => {
    if (!panel.classList.contains('is-open')) return;
    const input = liveSearchInput(panel);
    const query = input?.value.trim() || '';
    if (query.length < 2) return;

    const section = productSection(panel);
    const links = section ? Array.from(section.querySelectorAll('a.f24-live-search__item[data-f24-search-item]')) : [];
    if (!links.length) return;

    const signature = links.map((link) => productHandleFromLink(link)).join('|');
    const previous = livePanelState.get(panel);
    if (previous?.query === query && previous?.signature === signature) return;

    livePanelState.set(panel, { query, signature });
    try {
      const rankMap = await fetchLiveRankMap(query);
      if (input?.value.trim() !== query || !panel.classList.contains('is-open')) return;
      applyLiveRanks(panel, query, rankMap);
    } catch (_) {
      // Fail open: Shopify relevance order remains untouched when the stock probe is unavailable.
    }
  };

  const observeLivePanel = (panel) => {
    if (panel.dataset.f24StockRankObserver === 'true') return;
    panel.dataset.f24StockRankObserver = 'true';
    const panelObserver = new MutationObserver(() => {
      window.setTimeout(() => rankLivePanel(panel), 0);
    });
    panelObserver.observe(panel, { childList: true, subtree: true });
    window.setTimeout(() => rankLivePanel(panel), 0);
  };

  const discoverLivePanels = () => {
    document.querySelectorAll(LIVE_PANEL_SELECTOR).forEach(observeLivePanel);
    livePanelDiscoveryObserver?.disconnect();
    livePanelDiscoveryObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(LIVE_PANEL_SELECTOR)) observeLivePanel(node);
          node.querySelectorAll?.(LIVE_PANEL_SELECTOR).forEach(observeLivePanel);
        });
      });
    });
    livePanelDiscoveryObserver.observe(document.body, { childList: true, subtree: true });
  };

  const init = () => {
    window.F24SearchStockSort = { version: VERSION, liveRanking: 'eigenlager_available_unavailable' };
    addUnavailableLastToForms();
    discoverLivePanels();
    if (enforceUnavailableLastOnSearchUrl()) return;
    sortSearchGrid();
    observeSearchGrid();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    addUnavailableLastToForms();
    discoverLivePanels();
    scheduleSort();
    observeSearchGrid();
  });
})();
